import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { PageTransition } from "@/components/PageTransition";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const QuickReference = () => {
  useScrollToHash();
  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        <main className="container mx-auto max-w-5xl px-6 py-12">
          <div className="mb-4">
            <Link
              to="/field-guide"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Field Guide
            </Link>
          </div>

          <div className="mb-12">
            <h1 className="mb-4 text-4xl font-bold">Quick Reference</h1>
            <p className="text-xl text-muted-foreground">
              Fast lookup for status indicators, terminology, and shortcuts.
            </p>
          </div>

          <div className="space-y-12">
            {/* Invitation Status */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Invitation status indicators</CardTitle>
                <CardDescription>Status meanings for crew invitation emails</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Status</TableHead>
                      <TableHead className="w-[100px]">Indicator</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Pending</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-muted-foreground"></div>
                      </TableCell>
                      <TableCell>Invitation not yet sent to the crew member</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Sent</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                      </TableCell>
                      <TableCell>Email sent, waiting for delivery confirmation</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Delivered</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-primary"></div>
                      </TableCell>
                      <TableCell>Email successfully delivered to inbox</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Opened</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-secondary"></div>
                      </TableCell>
                      <TableCell>Crew member opened the invitation email</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Failed</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-destructive"></div>
                      </TableCell>
                      <TableCell>Email delivery failed - check the email address</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Assignment Status */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Assignment status</CardTitle>
                <CardDescription>Visual indicators for assignment states</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Status</TableHead>
                      <TableHead className="w-[100px]">Indicator</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Upcoming</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-muted"></div>
                      </TableCell>
                      <TableCell>Assignment hasn't started yet</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Starting soon</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-amber-500"></div>
                      </TableCell>
                      <TableCell>Assignment starts within reminder window</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">In progress</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 animate-pulse rounded-full bg-primary"></div>
                      </TableCell>
                      <TableCell>Assignment is currently active</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Completed</TableCell>
                      <TableCell>
                        <div className="h-3 w-3 rounded-full bg-muted-foreground/30"></div>
                      </TableCell>
                      <TableCell>Assignment has ended</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Crew Functions */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Common crew functions</CardTitle>
                <CardDescription>Typical roles and their responsibilities</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Function</TableHead>
                      <TableHead>Typical responsibilities</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Camera operator</TableCell>
                      <TableCell>Recording video of performances and events</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Photographer</TableCell>
                      <TableCell>Capturing still photos of artists and audience</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Stage manager</TableCell>
                      <TableCell>Coordinating stage activities and artist transitions</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Runner</TableCell>
                      <TableCell>Assisting with transport and quick tasks</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Interviewer</TableCell>
                      <TableCell>Conducting artist interviews</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Social media</TableCell>
                      <TableCell>Creating and posting real-time content</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Audio tech</TableCell>
                      <TableCell>Managing audio recording equipment</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Stage Zone Types */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Map zone types</CardTitle>
                <CardDescription>Different zone types on the festival map</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Zone Type</TableHead>
                      <TableHead>Purpose</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Stage zone</TableCell>
                      <TableCell>
                        Performance area - used for tracking crew presence at stages
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Festival area</TableCell>
                      <TableCell>Overall event boundary - used for geofencing</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Special location</TableCell>
                      <TableCell>
                        Points of interest like catering, media office, backstage
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Notification Types */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Notification types</CardTitle>
                <CardDescription>Push notifications you may receive</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Notification</TableHead>
                      <TableHead>When Sent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Assignment reminder</TableCell>
                      <TableCell>X minutes before your assignment starts (configurable)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">New assignment</TableCell>
                      <TableCell>When a producer adds a new assignment to your schedule</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Updated assignment</TableCell>
                      <TableCell>When an existing assignment is modified</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Deleted assignment</TableCell>
                      <TableCell>When an assignment is removed from your schedule</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Announcement</TableCell>
                      <TableCell>Important messages from the producer</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Emergency alert</TableCell>
                      <TableCell>Urgent notifications requiring immediate attention</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Time Formats */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>Time format reference</CardTitle>
                <CardDescription>Understanding time displays in PROduce</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Format</TableHead>
                      <TableHead className="w-[150px]">Example</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">24-hour</TableCell>
                      <TableCell>14:30</TableCell>
                      <TableCell>Default format. No AM/PM designation.</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">12-hour</TableCell>
                      <TableCell>2:30 PM</TableCell>
                      <TableCell>Can be enabled in settings.</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">After midnight</TableCell>
                      <TableCell>25:30 or 01:30+</TableCell>
                      <TableCell>Times after midnight on multi-day events.</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* GPS Accuracy */}
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle>GPS accuracy indicators</CardTitle>
                <CardDescription>Understanding location accuracy on maps</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[150px]">Accuracy</TableHead>
                      <TableHead className="w-[150px]">Circle Size</TableHead>
                      <TableHead>Typical cause</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Excellent</TableCell>
                      <TableCell>&lt; 10m</TableCell>
                      <TableCell>Clear sky, good GPS signal</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Good</TableCell>
                      <TableCell>10-25m</TableCell>
                      <TableCell>Some obstructions, indoor/outdoor transition</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Fair</TableCell>
                      <TableCell>25-50m</TableCell>
                      <TableCell>Indoor, dense buildings, low power mode</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Poor</TableCell>
                      <TableCell>&gt; 50m</TableCell>
                      <TableCell>Deep indoor, underground, heavy interference</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </main>

        {/* Footer */}
        <footer className="mt-12 border-t border-border py-8">
          <div className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            <p>PROduce Field Guide — Quick Reference</p>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default QuickReference;
